/**
 * The composer's target chip (refinement spec WP-5): `⌥1 Viko ▾`, the tab's key and the project,
 * plus whatever route is pinned ("Viko · Codex · Mac"). "Auto · Viko picks the agent & machine" lives
 * in its menu. Click (or ⌥R) opens a popover, anchored to the chip on desktop and centred on a phone (never a bottom sheet), to pin
 * the agent, machine, provider account and model. Every choice defaults to Auto. The choice applies
 * to this bark only, or, with "Make this the default for <project>", to the project.
 */
import type { BarkRouting } from '@viko/protocol'
import { Button, modelsFor } from '@viko/transcript-ui'
import { ChevronDown, Route } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { AUTO_ROUTE_LABEL, cleanRouting, routeLabel } from '../../lib/bark-route.js'
import { useMachines, useProject } from '../../lib/queries.js'
import { suggestRoute, useRoutingOutcomes, useRoutingPreferences } from '../../lib/routing-prefs.js'
import { Keys } from '../Keys.js'

export interface RouteChipProps {
  projectId: string
  projectName: string
  /** What this bark would use now: its override, else the project or global default. */
  value: BarkRouting
  /** Apply to this bark only. */
  onChange: (r: BarkRouting) => void
  /** Save as the project's default (encrypted setting); absent when it cannot be stored. */
  onMakeDefault?: (r: BarkRouting) => Promise<void> | void
  /** The text typed so far: "why Auto" explains what Auto would do with it. */
  draft?: string
  /** The tab's switch key (`⌥1`), shown as a keycap before the project name. */
  tabKey?: string | undefined
  disabled?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

const AGENTS = [
  { id: undefined, label: 'Auto' },
  { id: 'claude', label: 'Claude' },
  { id: 'codex', label: 'Codex' },
] as const

function Group({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="m-0 grid gap-1.5 border-0 p-0">
      <legend className="mb-1 p-0 text-[14px] font-semibold text-text-2">{legend}</legend>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </fieldset>
  )
}

function Choice({
  name,
  label,
  detail,
  checked,
  onSelect,
}: {
  name: string
  label: string
  detail?: string
  checked: boolean
  onSelect: () => void
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[14px] focus-within:outline focus-within:outline-2 focus-within:outline-accent ${
        checked
          ? 'border-accent bg-accent-soft text-accent-text'
          : 'border-border-subtle bg-surface-1 text-text-1'
      }`}
    >
      <input type="radio" name={name} checked={checked} onChange={onSelect} className="sr-only" />
      <span>{label}</span>
      {detail ? <span className={checked ? '' : 'text-text-2'}>{detail}</span> : null}
    </label>
  )
}

export function RouteChip({
  projectId,
  projectName,
  value,
  onChange,
  onMakeDefault,
  draft = '',
  tabKey,
  disabled,
  open,
  onOpenChange,
}: RouteChipProps) {
  const id = useId()
  const chip = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const machinesQ = useMachines()
  const project = useProject(projectId).data
  const prefs = useRoutingPreferences()
  const outcomes = useRoutingOutcomes()
  const [pending, setPending] = useState<BarkRouting>(cleanRouting(value))
  const [asDefault, setAsDefault] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const machines = useMemo(() => {
    const all = machinesQ.data ?? []
    const mine = all.filter(
      (m) =>
        project?.machines?.includes(m.id) || m.checkouts?.some((c) => c.projectId === projectId),
    )
    return mine.length ? mine : all
  }, [machinesQ.data, project, projectId])
  const machineName = (mid: string) => machinesQ.data?.find((m) => m.id === mid)?.name
  const accounts = useMemo(
    () =>
      machines.flatMap((m) =>
        (m.providerAccounts ?? [])
          .filter((a) => a.enabled)
          .map((a) => ({ ...a, machineId: m.id, machineName: m.name })),
      ),
    [machines],
  )
  const shownAccounts = accounts.filter(
    (a) =>
      (!pending.agent || a.provider === pending.agent) &&
      (!pending.machineId || a.machineId === pending.machineId),
  )
  const models = pending.agent === 'codex' ? [] : modelsFor('claude')

  // Open: start from the current value, focus the panel; close: give focus back to the chip.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setPending(cleanRouting(value))
      setAsDefault(false)
      setError('')
      queueMicrotask(() => panel.current?.querySelector<HTMLElement>('input:checked')?.focus())
    }
    if (!open && wasOpen.current) chip.current?.focus()
    wasOpen.current = open
  }, [open, value])
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      const t = e.target as Node
      if (!panel.current?.contains(t) && !chip.current?.contains(t)) onOpenChange(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open, onOpenChange])

  const explanation = useMemo(() => {
    const auto = machines.map((m) => ({ id: m.id, name: m.name, online: m.online }))
    const s = prefs.data
      ? suggestRoute(draft, projectId, auto, { prefs: prefs.data, outcomes: outcomes.data })
      : undefined
    return (
      s?.explanation ??
      'Auto picks by the task: your routing rules first, then what has worked before, then the defaults.'
    )
  }, [draft, projectId, machines, prefs.data, outcomes.data])

  const set = (patch: Partial<BarkRouting>) => {
    const next: BarkRouting = { ...pending, ...patch }
    // Keep the four choices consistent: an account belongs to one agent on one machine.
    const acct = accounts.find(
      (a) => a.id === next.accountId && (!next.machineId || a.machineId === next.machineId),
    )
    if (next.accountId && (!acct || (next.agent && acct.provider !== next.agent)))
      delete next.accountId
    if (next.agent === 'codex') delete next.model
    if (next.model && next.agent && !modelsFor(next.agent).some((m) => m.id === next.model))
      delete next.model
    setPending(cleanRouting(next))
  }

  const done = async () => {
    const r = cleanRouting(pending)
    setError('')
    if (asDefault && onMakeDefault) {
      setSaving(true)
      try {
        await onMakeDefault(r)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not save the default.')
        setSaving(false)
        return
      }
      setSaving(false)
    }
    onChange(r)
    onOpenChange(false)
  }

  const label = routeLabel(value, machineName)
  const summary = label || AUTO_ROUTE_LABEL
  return (
    <span className="relative inline-flex min-w-0">
      <button
        ref={chip}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? `${id}-panel` : undefined}
        aria-keyshortcuts="Alt+R"
        aria-label={`${projectName}${label ? ` · ${label}` : ''}: target and route`}
        title={`${projectName} · ${summary}. Choose the agent, machine, account and model (⌥R)`}
        className="lk-route-chip lk-target-chip flex min-w-0 items-center gap-1.5 rounded-full border border-border-subtle bg-surface-2 px-2.5 py-1 text-[14px] text-text-1 hover:bg-surface-1"
        onClick={() => onOpenChange(!open)}
      >
        {tabKey ? <Keys k={tabKey} className="lk-target-chip__k" /> : null}
        <span className="min-w-0 truncate">
          {projectName}
          {label ? <span className="text-text-2"> · {label}</span> : null}
        </span>
        <ChevronDown size={12} aria-hidden="true" className="shrink-0 text-text-2" />
      </button>
      {open ? (
        <div
          ref={panel}
          id={`${id}-panel`}
          role="dialog"
          aria-label={`Route for this bark in ${projectName}`}
          className="fixed inset-x-4 top-1/2 z-50 grid max-h-[80vh] -translate-y-1/2 gap-3 overflow-y-auto rounded-xl border border-border-subtle bg-surface-1 p-4 shadow-lg sm:absolute sm:inset-x-auto sm:bottom-full sm:left-0 sm:top-auto sm:mb-2 sm:w-[380px] sm:translate-y-0"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation()
              onOpenChange(false)
            }
          }}
        >
          <p
            className="m-0 flex items-center gap-1.5 text-[14px] text-text-1"
            data-testid="route-now"
          >
            <Route size={13} aria-hidden="true" className="shrink-0 text-text-2" />
            {summary}
          </p>
          <p className="m-0 text-[14px] text-text-2" data-testid="route-why">
            <strong className="text-text-1">Why Auto:</strong> {explanation}
          </p>
          <Group legend="Agent">
            {AGENTS.map((a) => (
              <Choice
                key={a.label}
                name={`${id}-agent`}
                label={a.label}
                checked={pending.agent === a.id}
                onSelect={() => set({ agent: a.id as BarkRouting['agent'] })}
              />
            ))}
          </Group>
          <Group legend="Machine">
            <Choice
              name={`${id}-machine`}
              label="Auto"
              checked={!pending.machineId}
              onSelect={() => set({ machineId: undefined })}
            />
            {machines.map((m) => (
              <Choice
                key={m.id}
                name={`${id}-machine`}
                label={m.name}
                detail={m.online ? 'online' : 'offline'}
                checked={pending.machineId === m.id}
                onSelect={() => set({ machineId: m.id })}
              />
            ))}
          </Group>
          <Group legend="Provider account">
            <Choice
              name={`${id}-account`}
              label="Auto"
              checked={!pending.accountId}
              onSelect={() => set({ accountId: undefined })}
            />
            {shownAccounts.map((a) => (
              <Choice
                key={`${a.machineId}:${a.id}`}
                name={`${id}-account`}
                label={a.label}
                detail={`${a.provider === 'claude' ? 'Claude' : 'Codex'} · ${a.machineName}`}
                checked={pending.accountId === a.id && pending.machineId === a.machineId}
                onSelect={() => set({ accountId: a.id, machineId: a.machineId, agent: a.provider })}
              />
            ))}
          </Group>
          <Group legend="Model">
            <Choice
              name={`${id}-model`}
              label="Auto"
              checked={!pending.model}
              onSelect={() => set({ model: undefined })}
            />
            {models.map((m) => (
              <Choice
                key={m.id}
                name={`${id}-model`}
                label={m.label}
                checked={pending.model === m.id}
                onSelect={() => set({ model: m.id })}
              />
            ))}
          </Group>
          {pending.agent === 'codex' ? (
            <p className="m-0 text-[14px] text-text-3">Codex picks its own model.</p>
          ) : null}
          {onMakeDefault ? (
            <label className="flex items-center gap-2 text-[14px] text-text-1">
              <input
                type="checkbox"
                checked={asDefault}
                onChange={(e) => setAsDefault(e.target.checked)}
              />
              Make this the default for {projectName}
            </label>
          ) : (
            <p className="m-0 text-[14px] text-text-3">This choice applies to this bark only.</p>
          )}
          {error ? (
            <p role="alert" className="m-0 text-[14px] text-status-wait">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-between gap-2">
            <Button size="sm" variant="ghost" onClick={() => setPending({})}>
              Reset to Auto
            </Button>
            <span className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" disabled={saving} onClick={() => void done()}>
                Done
              </Button>
            </span>
          </div>
        </div>
      ) : null}
    </span>
  )
}

/** Read-only chip for surfaces without a bark store (the capture-sheet fallback). */
export function StaticRouteChip() {
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-[14px] text-text-3">
      <Route size={13} aria-hidden="true" className="shrink-0" />
      <span>{AUTO_ROUTE_LABEL}</span>
    </span>
  )
}
